import { NgFor, NgIf, NgStyle } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NgxGraphModule, Node, Edge, DagreNodesOnlyLayout, Layout } from '@kr0san89/ngx-graph';
import * as shape from 'd3-shape';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { ConversationFlowStepResponse } from '../../../../models/responses/conversation-flow-step-response';
import { ConversationStepService } from '../../../../services/conversation-step.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AiAgentResponse } from '../../../../models/responses/ai-agent-response';
import { ConversationFlowStepRequest } from '../../../../models/requests/Conversation-flow-step-request';
import { ConversationFlowArcRequest } from '../../../../models/requests/Conversation-flow-arc-step';
import { ConversationFlowArcResponse } from '../../../../models/responses/conversational-flow-arc-response';
import { DiagramFlowChildrenSelectionDialogComponent } from '../../diagram-flow-children-selection-dialog/diagram-flow-children-selection-dialog.component';
import { TestAssistantComponent } from '../../ai-aigent-component/test-assistant/test-assistant.component';
import { WarningDialogComponent } from '../../../general-dialogs-components/warning-dialog/warning-dialog.component';
import { CONFIRMATION_DIALOG_TEXT, ConfirmationDialogComponent } from '../../../general-dialogs-components/confirmation-dialog/confirmation-dialog.component';
import { scrollToId, scrollToTopSmooth } from '../../../../utilities/scrollingUtil';
import { ChatbotComponent } from '../../chatbot/chatbot.component';
import { ActionAdderDialogComponent } from '../../action-adder-dialog/action-adder-dialog.component';
import { ConversationFlowStepIntegrations } from '../../../../models/responses/conversation-flow-step-integrations';
import { CAL_ICON, GHL_ICON,MAKE_ICON, GOOGLE_SHEET_ICON, NOTIFICATION_ICON, ZAPIER_ICON } from '../../../../app.component';

export interface Step {
  name: string;
  callback: Function;
}

enum IntegrationType {
  CAL = 'CAL',
  GHL = 'GHL',
  ZAPIER = 'ZAPIER',
  GOOGLE_SHEET = 'GOOGLE SHEET',
  MAKE = 'MAKE',
  NOTIFICATION = "NOTIFICATION"
}

interface Integration {
  id: number;
  name: string;
  IntegrationType: IntegrationType;
  removeCallBack: Function;
  icon: string;
}

@Component({
  selector: 'app-diagram-flow',
  standalone: true,
  imports: [
    NgxGraphModule,
    NgIf,
    NgFor,
    NgStyle,
    MatInputModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    FormsModule,
    MatDividerModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TestAssistantComponent,
    ChatbotComponent,
    MatDividerModule
  ],
  templateUrl: './diagram-flow.component.html',
  styleUrl: './diagram-flow.component.scss'
})

export class DiagramFlowComponent {
  @ViewChild('graphDiv') graphDiv!: ElementRef<HTMLDivElement>;
  @Input() aiAgent!: AiAgentResponse;
  isLoading: boolean = false;
  graphWidth!: number;
  graphHeight: number = 1500;
  nodeQuestionForm: FormGroup | undefined;
  currentParentNode: Node | undefined;
  isNodeBeingEdited: boolean = false;
  chatbotBeingTested: boolean = false;
  private _resizeObserver!: ResizeObserver;
  nodes: Node[] = [];
  links: Edge[] = [];
  layoutSettings = {
    orientation: 'TB'
  };
  curve: any = shape.curveLinear;
  layout: Layout = new DagreNodesOnlyLayout();
  childrenOfSelectedNode: { name: string, directive: string, condition: string, id: number }[] = [];
  integrationsOfSelectedNode: Integration[] = [];
  addActionButtonX: number = 30;
  addActionButtonY: number = 40;

  constructor(
    public dialog: MatDialog,
    private changeDetectorRef: ChangeDetectorRef,
    private formBuilder: FormBuilder,
    private conversationStepService: ConversationStepService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit() {
    this.initGraph(this.aiAgent.steps);
  }

  ngAfterViewInit() {
    this.initResizeObserver();
  }

  initGraph(steps: ConversationFlowStepResponse[]) {
    if (steps.length > 5)
      this.graphHeight = 1500
    else
      this.graphHeight = 700;

    const nodesToAdd: Node[] = [];
    const edgesToAdd: Edge[] = [];
    this.nodes = [];
    this.links = [];

    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      const node: Node = {
        id: step.id.toString(),
        label: step.name,
        data: {
          step,
          preview1: step.directive.slice(0, 30),
          preview2: step.directive.slice(30, 60)
        }
      }

      for (let arc of step.arcs) {
        const edge: Edge = {
          source: arc.currentStepId!.toString(),
          target: arc.nextStepId!.toString(),
          data: {
            arc: arc,
            step
          }
        }

        edgesToAdd.push(edge);
      }

      nodesToAdd.push(node);
    }

    this.nodes = nodesToAdd;
    this.links = edgesToAdd;
  }

  // NODE FORM CREATION

  initRootNodeCreationForm() {
    this.nodeQuestionForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.maxLength(15)]],
      conditionsArray: this.formBuilder.array([]),
      directive: ['', Validators.required],
      isScheduling: [false]
    });

    this.addListenerToIsScheduling();
  }

  initChildNodeCreationForm() {
    this.nodeQuestionForm = this.formBuilder.group({
      parentId: this.currentParentNode!.id,
      name: ['', [Validators.required, Validators.maxLength(15)]],
      conditionsArray: this.formBuilder.array([]),
      directive: ['', Validators.required],
      isScheduling: [false]
    });

    this.conditionsArray.push(this.formBuilder.group({
      id: '',
      condition: '',
      parentName: this.currentParentNode?.data.name,
      parentId: this.currentParentNode?.id
    }));

    this.addListenerToIsScheduling();
  }

  addFilledConditionsArray(step: ConversationFlowStepResponse) {
    for (let arc of step.incomingArcs) {
      let parent: ConversationFlowStepResponse = this.aiAgent.steps.find(step => step.id == arc.currentStepId)!;

      this.conditionsArray.push(this.formBuilder.group({
        id: arc.id,
        condition: arc.condition,
        parentName: parent.name,
        parentId: parent.id
      }));
    }
  }

  get conditionsArray(): FormArray<FormGroup> {
    return this.nodeQuestionForm?.controls['conditionsArray'] as FormArray<FormGroup>;
  }

  onEditNodeCreationForm(node: Node) {
    scrollToId('inputName');
    const step: ConversationFlowStepResponse = node.data.step;
    const link: Edge | undefined = this.links.find(edge => edge.target == node.id);
    this.childrenOfSelectedNode = this.getAllChildrenForNode(parseInt(node.id));
    this.setAllIntegrationsOfNode(step.integrations);
    this.isNodeBeingEdited = true;

    if (link)
      this.currentParentNode = this.nodes.find(node => node.id == link.source);
    else
      this.currentParentNode = undefined;

    this.nodeQuestionForm = this.formBuilder.group({
      id: step.id,
      name: [step.name, [Validators.required, Validators.minLength(1), Validators.maxLength(15)]],
      conditionsArray: this.formBuilder.array([]),
      directive: [step.directive, Validators.required],
      isScheduling: step.isScheduling
    });

    this.addListenerToIsScheduling();
    this.addFilledConditionsArray(step);
  }

  private addListenerToIsScheduling() {
    this.nodeQuestionForm!.controls['isScheduling'].valueChanges.subscribe((value) => {

      if (value == true) {
        this.dialog.open(WarningDialogComponent, {
          width: '25%',
          autoFocus: false,
          data: { title: 'Appointment Warning', data: `First, make sure to have added the cal integration in the integration tab when checking this box. Second make sure to have added the step that ask for first name, last name, email and phone number. If both conditions are not respected the assistant may malfunction.` }
        });
      }
    });
  }

  // NODE SERVICES

  private getAllChildrenForNode(nodeId: number): { name: string, directive: string, condition: string, id: number }[] {
    const children: { name: string, directive: string, condition: string, id: number }[] = [];

    this.links.forEach(link => {
      if (link.source == nodeId.toString()) {
        const arc: ConversationFlowArcResponse = link.data.arc;

        this.nodes.forEach(node => {
          if (node.id == arc.nextStepId?.toString()) {
            const step = node.data.step;
            children.push({ name: step.name, directive: step.directive, condition: arc.condition, id: step.id });
          }
        });
      }
    });

    return children;
  }

  onAddChildNodeCreationForm(parentNode: Node) {
    this.currentParentNode = parentNode;
    this.isNodeBeingEdited = false;
    scrollToId('inputName');
    this.addQuestionNode();
  }

  onNodeCreationCancel() {
    this.nodeQuestionForm = undefined;
    this.isNodeBeingEdited = false;
  }

  addQuestionNode() {
    if (this.nodes.length > 0)
      this.initChildNodeCreationForm();
    else
      this.initRootNodeCreationForm();
  }

  onNodeCreationSubmit() {
    if (this.nodeQuestionForm?.valid) {
      const values = this.nodeQuestionForm!.value;
      this.isLoading = true;

      if (this.nodes.length == 0)
        this.addRootStep(values);
      else
        this.updateOrCreateChildStep(values);
    }
  }

  disconnectParentFromChild(childId: number) {
    const childStep: ConversationFlowStepResponse = this.aiAgent.steps.find(node => childId == node.id)!;
    const parentStep: ConversationFlowStepResponse = this.aiAgent.steps.find(node => parseInt(this.nodeQuestionForm?.controls['id'].value) == node.id)!;

    if (childStep.incomingArcs.length > 1)
      this.deleteArc(parentStep, childId);
    else
      this.onNodeDelete(childId.toString());
  }

  onConnectChild() {
    const parent: ConversationFlowStepResponse = this.aiAgent.steps.find(step => step.id == parseInt(this.nodeQuestionForm?.controls['id'].value))!;
    const aiAgentId: number = this.aiAgent.id;
    const directChildrenId: number[] = [parent.id, ...parent.arcs.map(arc => arc.nextStepId!)];
    const availableChildren: ConversationFlowStepResponse[] = this.aiAgent.steps.filter(step => {
      return !directChildrenId.some(child => child == step.id);
    });

    const dialog = this.dialog.open(DiagramFlowChildrenSelectionDialogComponent, {
      width: '80%',
      autoFocus: false,
      data: { parent, aiAgentId, availableChildren }
    });

    dialog.afterClosed().subscribe((steps) => {
      if (steps)
        this.resetNodeForm(steps);
    });
  }

  onNodeDelete(nodeId: string) {
    this.dialog.open(ConfirmationDialogComponent, {
      width: '25%',
      autoFocus: false,
      data: { title: 'Step deletion confirmation', data: 'Are you sure you want to delete this step?' }
    }).afterClosed().subscribe((result) => {
      if (result == CONFIRMATION_DIALOG_TEXT) {
        this.isLoading = true;

        this.conversationStepService.deleteStep(parseInt(nodeId), this.aiAgent.id).subscribe({
          next: (steps) => {
            this.openSnackBar('Step deleted successfully');
            this.isLoading = false;
            this.resetNodeForm(steps);
          },
          error: () => {
            alert('There was a server error, contact support');
            this.isLoading = false;
          }
        });
      }
    });
  }

  toggleTestAI() {
    this.chatbotBeingTested = !this.chatbotBeingTested;
  }

  // CONNECT ACTION FUNCTIONS
  onConnectActionClicked() {
    const parent: ConversationFlowStepResponse = this.aiAgent.steps.find(step => step.id == parseInt(this.nodeQuestionForm?.controls['id'].value))!;

    this.dialog.open(ActionAdderDialogComponent, {
      width: '80%',
      autoFocus: false,
      data: { aiAgent: this.aiAgent, step: parent }
    }).afterClosed().subscribe(data => {
      if (data.zap && data.steps) {
        parent.integrations.zaps.push(data.zap);
        this.setAllIntegrationsOfNode(parent.integrations);
        this.aiAgent.steps = data.steps;
        this.initGraph(data.steps);
        this.openSnackBar('Action added successfully');
      } else if (data.calComIntegration && data.steps) {
        parent.integrations.calComIntegrations = data.calComIntegration;
        this.setAllIntegrationsOfNode(parent.integrations);
        this.aiAgent.steps = data.steps;
        this.initGraph(data.steps);
        this.openSnackBar('Action added successfully');
      } else if (data.ghlIntegration && data.steps) {
        parent.integrations.goHighLevelIntegration = data.ghlIntegration;
        this.setAllIntegrationsOfNode(parent.integrations);
        this.aiAgent.steps = data.steps;
        this.initGraph(data.steps);
        this.openSnackBar('Action added successfully');
      }else if (data.notificationIntegration && data.steps) {
        parent.integrations.notification = data.notificationIntegration;
        this.setAllIntegrationsOfNode(parent.integrations);
        this.aiAgent.steps = data.steps;
        this.initGraph(data.steps);
        this.openSnackBar('Action added successfully');
      } else if (data.make && data.steps) {
        parent.integrations.makes.push(data.make);
        this.setAllIntegrationsOfNode(parent.integrations);
        this.aiAgent.steps = data.steps;
        this.initGraph(data.steps);
        this.openSnackBar('Action added successfully');
      }else if(data.googleSheetIntegration){
        parent.integrations.googleSheetsIntegraton = data.googleSheetIntegration;
        this.setAllIntegrationsOfNode(parent.integrations);
        this.aiAgent.steps = data.steps;
        this.initGraph(data.steps);
        this.openSnackBar('Action added successfully');
      }
    });
  }

  // PRIVATE FUNCTIONS

  private onDisconnectZapIntegration(zapId: number, integrations: ConversationFlowStepIntegrations) {
    this.conversationStepService.removeZapFromStep(zapId, integrations.conversationFlowStepId, this.aiAgent.id).subscribe({
      next: (steps) => {
        this.aiAgent.steps = steps;
        this.initGraph(steps);
        this.openSnackBar('Action was removed successfully');
        this.integrationsOfSelectedNode = this.integrationsOfSelectedNode.filter(integration => {
          return !(integration.IntegrationType == IntegrationType.ZAPIER && integration.id == zapId);
        });
      },
      error: () => {
        alert('Server error, contact support');
      }
    });
  }

  private onDisconnectMakeIntegration(makeId: number, integrations: ConversationFlowStepIntegrations) {
    this.conversationStepService.removeMakeFromStep(makeId, integrations.conversationFlowStepId, this.aiAgent.id).subscribe({
      next: (steps) => {
        this.aiAgent.steps = steps;
        this.initGraph(steps);
        this.openSnackBar('Action was removed successfully');
        this.integrationsOfSelectedNode = this.integrationsOfSelectedNode.filter(integration => {
          return !(integration.IntegrationType == IntegrationType.MAKE && integration.id == makeId);
        });
      },
      error: () => {
        alert('Server error, contact support');
      }
    });
  }

  private onDisconnectCalIntegration(calId: number, integrations: ConversationFlowStepIntegrations) {
    this.conversationStepService.removeCalFromStep(calId, integrations.conversationFlowStepId, this.aiAgent.id).subscribe({
      next: (steps) => {
        this.aiAgent.steps = steps;
        this.initGraph(steps);
        this.openSnackBar('Action was removed successfully');
        this.integrationsOfSelectedNode = this.integrationsOfSelectedNode.filter(integration => {
          return !(integration.IntegrationType == IntegrationType.CAL && integration.id == calId);
        });
      },
      error: () => {
        alert('Server error, contact support');
      }
    });
  }

  private onDisconnectGhlntegration(ghlId: number, integrations: ConversationFlowStepIntegrations) {
    this.conversationStepService.removeGHLFromStep(ghlId, integrations.conversationFlowStepId, this.aiAgent.id).subscribe({
      next: (steps) => {
        this.aiAgent.steps = steps;
        this.initGraph(steps);
        this.openSnackBar('Action was removed successfully');
        this.integrationsOfSelectedNode = this.integrationsOfSelectedNode.filter(integration => {
          return !(integration.IntegrationType == IntegrationType.GHL && integration.id == ghlId)
        });
      },
      error: () => {
        alert('Server error, contact support');
      }
    });
  }


  private onDisconnectNotificationIntegration(notificationId: number, integrations: ConversationFlowStepIntegrations) {
    this.conversationStepService.removeNotificationFromStep(notificationId, integrations.conversationFlowStepId, this.aiAgent.id).subscribe({
      next: (steps) => {
        this.aiAgent.steps = steps;
        this.initGraph(steps);
        this.openSnackBar('Action was removed successfully');
        this.integrationsOfSelectedNode = this.integrationsOfSelectedNode.filter(integration => {
          return !(integration.IntegrationType == IntegrationType.NOTIFICATION && integration.id == notificationId)
        });
      },
      error: () => {
        alert('Server error, contact support');
      }
    });
  }

  private onDisconnectGoogleSheetIntegration(googleSheetIntegrationId: number, integrations: ConversationFlowStepIntegrations) {
    this.conversationStepService.removeGoogleSheetFromStep(googleSheetIntegrationId, integrations.conversationFlowStepId, this.aiAgent.id).subscribe({
      next: (steps) => {
        this.aiAgent.steps = steps;
        this.initGraph(steps);
        this.openSnackBar('Action was removed successfully');
        this.integrationsOfSelectedNode = this.integrationsOfSelectedNode.filter(integration => {
          return !(integration.IntegrationType == IntegrationType.GOOGLE_SHEET && integration.id == googleSheetIntegrationId)
        });
      },
      error: () => {
        alert('Server error, contact support');
      }
    });
  }

  private deleteArc(parentStep: ConversationFlowStepResponse, childId: number) {
    this.isLoading = true;
    const arcToDelete: ConversationFlowArcResponse = parentStep.arcs.find((arc: ConversationFlowArcResponse) => arc.nextStepId == childId)!;

    this.conversationStepService.deleteArc(arcToDelete.id!, this.aiAgent.id).subscribe({
      next: (steps) => {
        this.isLoading = false;
        this.openSnackBar('child was disconnected successfully');
        this.resetNodeForm(steps);
      },
      error: () => {
        this.isLoading = false;
        alert('Server error contact support');
      }
    });
  }

  private updateOrCreateChildStep(values: any) {
    const step: ConversationFlowStepRequest = {
      id: parseInt(values['id']),
      name: values['name'],
      directive: values['directive'],
      aiAgentId: this.aiAgent.id,
      isScheduling: values['isScheduling'],
      incomingArcs: []
    };

    if (isNaN(step.id!))
      this.createChildStep(step);
    else
      this.updateStep(step);
  }

  private updateStep(step: ConversationFlowStepRequest) {
    const stepToEdit: ConversationFlowStepResponse = this.aiAgent.steps.find(step2 => step2.id == step.id)!;
    const arcs: { id: number, condition: string }[] = this.getArcsFromConditionsArray();

    if (stepToEdit.incomingArcs.length > 0) {
      for (let arc of stepToEdit.incomingArcs) {
        let condition: string = arcs.find(arc2 => arc2.id == arc.id)!.condition;

        if (arc.condition != condition) {
          arc.condition = condition;

          this.conversationStepService.updateArc(arc, this.aiAgent.id).subscribe(this.handleStepServiceRequest());
        }
      }
    }

    this.conversationStepService.updateStep(step).subscribe(this.handleStepServiceRequest());
  }

  private getArcsFromConditionsArray(): { id: number, condition: string, parentId: number }[] {
    const arcs: { id: number, condition: string, parentId: number }[] = [];

    for (let index = 0; index < this.conditionsArray.length; index++) {
      const element = this.conditionsArray.controls[index].value;

      arcs.push({
        id: parseInt(element['id']),
        condition: element['condition'],
        parentId: parseInt(element['parentId'])
      });
    }

    return arcs;
  }

  private createChildStep(childStep: ConversationFlowStepRequest) {
    childStep.id = undefined;
    const arcs: { id: number, condition: string, parentId: number }[] = this.getArcsFromConditionsArray();

    const childArc: ConversationFlowArcRequest = {
      condition: arcs[0].condition,
      parentId: arcs[0].parentId
    };

    childStep.incomingArcs?.push(childArc);

    this.conversationStepService.addChildStep(childStep).subscribe(this.handleStepServiceRequest());
  }

  private addRootStep(values: any) {
    const rootStep: ConversationFlowStepRequest = {
      name: values['name'],
      directive: values['directive'],
      aiAgentId: this.aiAgent.id,
      isScheduling: values['isScheduling']
    };

    this.conversationStepService.addRootStep(rootStep).subscribe(this.handleStepServiceRequest());
  }

  private handleStepServiceRequest() {
    this.isLoading = true;

    return {
      next: (steps: ConversationFlowStepResponse[]) => {
        this.openSnackBar('Step saved successfully');
        this.resetNodeForm(steps);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        alert('There was a server error, contact support');
      }
    };
  }

  private resetNodeForm(steps: ConversationFlowStepResponse[]) {
    this.nodeQuestionForm?.reset();
    this.nodeQuestionForm = undefined;
    this.currentParentNode = undefined;
    this.childrenOfSelectedNode = [];
    this.integrationsOfSelectedNode = [];
    this.aiAgent.steps = steps;
    this.isNodeBeingEdited = false;
    this.initGraph(steps);
  }

  private openSnackBar(message: string) {
    this.snackBar.open(message, 'Close', { duration: 5000 });
  }

  private setAllIntegrationsOfNode(integrations: ConversationFlowStepIntegrations) {
    this.integrationsOfSelectedNode = [];
    if (integrations.calComIntegrations) {
      this.integrationsOfSelectedNode.push({
        id: integrations.calComIntegrations.id,
        name: integrations.calComIntegrations.name,
        IntegrationType: IntegrationType.CAL,
        removeCallBack: () => {
          this.dialog.open(ConfirmationDialogComponent, {
            width: '25%',
            autoFocus: false,
            data: { title: 'Remove zap', data: 'Are you sure you want to remove this action from this step?' }
          }).afterClosed().subscribe(result => {
            if (result == CONFIRMATION_DIALOG_TEXT) {
              this.onDisconnectCalIntegration(integrations.calComIntegrations.id, integrations);
            }
          });
        },
        icon: CAL_ICON
      });
    }

    if (integrations.goHighLevelIntegration) {
      this.integrationsOfSelectedNode.push({
        id: integrations.goHighLevelIntegration.id,
        name: integrations.goHighLevelIntegration.name,
        IntegrationType: IntegrationType.GHL,
        removeCallBack: () => {
          this.dialog.open(ConfirmationDialogComponent, {
            width: '25%',
            autoFocus: false,
            data: { title: 'Remove GHL', data: 'Are you sure you want to remove this action from this step?' }
          }).afterClosed().subscribe(result => {
            if (result == CONFIRMATION_DIALOG_TEXT) {
              this.onDisconnectGhlntegration(integrations.goHighLevelIntegration.id, integrations);
            }
          });
        },
        icon: GHL_ICON
      });
    }

    if (integrations.notification) {
      this.integrationsOfSelectedNode.push({
        id: integrations.notification.id,
        name: integrations.notification.target,
        IntegrationType: IntegrationType.NOTIFICATION,
        removeCallBack: () => {
          this.dialog.open(ConfirmationDialogComponent, {
            width: '25%',
            autoFocus: false,
            data: { title: 'Remove notification', data: 'Are you sure you want to remove this action from this step?' }
          }).afterClosed().subscribe(result => {
            if (result == CONFIRMATION_DIALOG_TEXT) {
              this.onDisconnectNotificationIntegration(integrations.notification.id, integrations);
            }
          });
        },
        icon: NOTIFICATION_ICON
      });
    }
    if (integrations.googleSheetsIntegraton) {
      this.integrationsOfSelectedNode.push({
        id: integrations.googleSheetsIntegraton.id,
        name: "GOOGLE SHEET",
        IntegrationType: IntegrationType.GOOGLE_SHEET,
        removeCallBack: () => {
          this.dialog.open(ConfirmationDialogComponent, {
            width: '25%',
            autoFocus: false,
            data: { title: 'Remove Google Sheet', data: 'Are you sure you want to remove this action from this step?' }
          }).afterClosed().subscribe(result => {
            if (result == CONFIRMATION_DIALOG_TEXT) {
              this.onDisconnectGoogleSheetIntegration(integrations.googleSheetsIntegraton.id, integrations);
            }
          });
        },
        icon: GOOGLE_SHEET_ICON
      });
    }

    if (integrations.zaps && integrations.zaps.length > 0) {
      integrations.zaps.forEach(zap => {
        this.integrationsOfSelectedNode.push({
          id: zap.id!,
          name: zap.name,
          IntegrationType: IntegrationType.ZAPIER,
          removeCallBack: () => {
            this.dialog.open(ConfirmationDialogComponent, {
              width: '25%',
              autoFocus: false,
              data: { title: 'Remove zap', data: 'Are you sure you want to remove this action from this step?' }
            }).afterClosed().subscribe(result => {
              if (result == CONFIRMATION_DIALOG_TEXT) {
                this.onDisconnectZapIntegration(zap.id!, integrations);
              }
            });

          },
          icon: ZAPIER_ICON
        });
      });
    }
    if (integrations.makes && integrations.makes.length > 0) {
      integrations.makes.forEach(makes => {
        this.integrationsOfSelectedNode.push({
          id: makes.id!,
          name: makes.name,
          IntegrationType: IntegrationType.MAKE,
          removeCallBack: () => {
            this.dialog.open(ConfirmationDialogComponent, {
              width: '25%',
              autoFocus: false,
              data: { title: 'Remove make', data: 'Are you sure you want to remove this action from this step?' }
            }).afterClosed().subscribe(result => {
              if (result == CONFIRMATION_DIALOG_TEXT) {
                this.onDisconnectMakeIntegration(makes.id!, integrations);
              }
            });

          },
          icon: MAKE_ICON
        });
      });
    }

  }

  private initResizeObserver() {
    this._resizeObserver = new ResizeObserver(() => {
      this.graphWidth = this.graphDiv.nativeElement.offsetWidth;

      if (this.graphDiv.nativeElement.offsetHeight > 1500)
        this.graphHeight = this.graphDiv.nativeElement.offsetHeight;
    });

    this.graphWidth = this.graphDiv.nativeElement.offsetWidth;
    this.addActionButtonX = this.graphWidth / 2;
    this.changeDetectorRef.detectChanges();

    if (this.graphDiv) {
      this._resizeObserver.observe(this.graphDiv.nativeElement);
    }
  }
}
